/* CLEAR Kit personalization — native replacement for the Tepo/Qikify option app.
 *
 * WHAT IT MUST EMIT, and why. Verified against hq/server/src/ops/import.ts:
 *
 *   "Adult 1 Full Name" / "Adult 2 Full Name"
 *       parseAdults() reads these verbatim, trimming outer whitespace only. It
 *       deliberately preserves internal double spaces ("Karen  Lee", #162278)
 *       because that is what the customer typed and what goes on the binder.
 *
 *   "_biw_pers" = "1"
 *       import.ts:271 flips personalizationApp = true on ANY underscore-prefixed
 *       property. HQ uses that flag to separate "the customer declined a name"
 *       from "the option app never ran and the name is lost", and refuses to
 *       merge them. Emit only visible properties and every bundle order looks
 *       like an app failure and lands in Needs Attention. Not decoration.
 *
 * DECLINED NAME is the literal string "Not personalized" written INTO the name
 * property, never a blank and never a separate boolean. BUILDER_NO_NAME
 * (/^not\s*personali[sz]ed$/i) matches it and renderCoverSheet keys off it to
 * paint out the "Exclusively Prepared For" label. A boolean beside a blank name
 * reads to HQ as MISSING, not declined.
 *
 * STATELESS BY DESIGN. An earlier version did the work once in init() and kept
 * `form` and `rows` in a closure. On the live page that silently stopped
 * working: Dawn re-renders the main-product section (variant change, ?variant=
 * deep links, the section rendering API) and swaps the DOM out from under any
 * captured reference. The second-adult field stayed disabled, with no error in
 * the console and every individual piece of logic testing correct in isolation.
 * Everything below re-acquires its nodes on every tick instead, so a re-render
 * costs one 400ms cycle rather than breaking name capture until someone
 * notices. This field is the only thing between a customer and a blank binder,
 * so cheap and dumb beats clever.
 */
(function () {
  'use strict';

  var NOT_PERS = 'Not personalized';

  /* Dawn renders custom_liquid blocks OUTSIDE <product-form>, so inputs left
     where Liquid puts them submit NOTHING — verified live, closest('form') was
     null and no property reached the cart. Relocate into the real cart form:
     the one carrying both a variant id and the add button. */
  function cartForm() {
    var info = document.querySelector('.product__info-container') || document;
    var forms = [].slice.call(info.querySelectorAll('form[action*="/cart/add"]'));
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="add"]') && forms[i].querySelector('[name="id"]')) {
        return forms[i];
      }
    }
    return null;
  }

  function adultsWanted() {
    // Scope to the picker, not the form: relocating this block into the cart
    // form puts the variant control outside our subtree.
    var picker = document.querySelector('variant-selects, variant-radios')
      || document.querySelector('.product__info-container') || document;
    // V4: the product has two option groups (Kit, Adults). Look at every
    // checked radio, not just the first, which would be the Kit.
    var checked = picker.querySelectorAll('input[type="radio"]:checked');
    var sel = picker.querySelector('select');
    var txt = '';
    if (checked.length) {
      for (var i = 0; i < checked.length; i++) txt += ' ' + (checked[i].value || '');
    } else if (sel && sel.options[sel.selectedIndex]) {
      txt = sel.options[sel.selectedIndex].text;
    }
    return /two/i.test(txt) ? 2 : 1;
  }

  function bindSkip(row) {
    var box = row.querySelector('[data-biwpers-skip]');
    var field = row.querySelector('[data-biwpers-name]');
    if (!box || !field || box.getAttribute('data-bound')) return;
    box.setAttribute('data-bound', '1');
    box.addEventListener('change', function () {
      if (box.checked) {
        if (field.value !== NOT_PERS) field.setAttribute('data-prev', field.value || '');
        field.value = NOT_PERS;
        field.readOnly = true;
        field.setAttribute('aria-disabled', 'true');
      } else {
        field.value = field.getAttribute('data-prev') || '';
        field.removeAttribute('data-prev');
        field.readOnly = false;
        field.removeAttribute('aria-disabled');
      }
    });
  }

  function tick() {
    var root = document.querySelector('[data-biwpers]');
    if (!root) return;

    var form = cartForm();
    if (!form) {
      // Better no field at all than a field that quietly captures nothing.
      if (root.parentNode) root.parentNode.removeChild(root);
      return;
    }
    if (!form.contains(root)) {
      var submit = form.querySelector('[name="add"]');
      var anchor = submit ? (submit.closest('.product-form__buttons') || submit) : null;
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(root, anchor);
      else form.appendChild(root);
    }

    var n = adultsWanted();
    var rows = [].slice.call(root.querySelectorAll('[data-biwpers-row]'));
    rows.forEach(function (row, i) {
      var on = i < n;
      row.hidden = !on;
      // A hidden input still submits. An empty "Adult 2 Full Name" on a
      // one-adult order would read downstream as a second adult with no name.
      // Disabled inputs are omitted from the POST entirely.
      [].slice.call(row.querySelectorAll('input')).forEach(function (el) {
        el.disabled = !on;
      });
      bindSkip(row);
    });
    syncConfirm(root);
  }

  // Belt and braces beside the required attribute. Dawn submits the product form
  // through fetch; native validation runs for a real submit event, but anything
  // that calls form.submit() programmatically skips it, and a blank name is not
  // a recoverable mistake downstream - Shopify DISCARDS a non-underscore
  // property whose value is empty, so the name does not arrive empty, it does
  // not arrive at all (#162336). Block here rather than let that happen.
  function guardSubmit(e) {
    var root = document.querySelector('[data-biwpers]');
    if (!root) return;
    var form = root.closest('form');
    if (!form || e.target !== form) return;
    var sync = syncConfirm(root);
    var st = sync ? sync.state : personState(root);
    if (!st.complete && st.firstBlank) {
      e.preventDefault();
      e.stopImmediatePropagation();
      st.firstBlank.focus();
      if (st.firstBlank.reportValidity) st.firstBlank.reportValidity();
      return;
    }
    if (st.anyPersonalized && sync && sync.box && !sync.box.checked) {
      e.preventDefault();
      e.stopImmediatePropagation();
      sync.box.focus();
      if (sync.box.setCustomValidity) {
        sync.box.setCustomValidity('Please confirm the spelling. Names are printed exactly as entered.');
        if (sync.box.reportValidity) sync.box.reportValidity();
        setTimeout(function () { sync.box.setCustomValidity(''); }, 0);
      }
    }
  }


  // The builder's contract, brought to the product page: type a name, see what
  // will print, then confirm the spelling - or say you want no personalization.
  // Confirming spelling you have not entered is not a confirmation, so the box
  // stays disabled until every visible adult is answered, and a stale tick is
  // cleared the moment they are not (the builder learned that the hard way: a
  // ticked box over half-typed names that could not be unticked).
  function personState(root) {
    var rows = [].slice.call(root.querySelectorAll('[data-biwpers-row]'));
    var visible = rows.filter(function (r) { return !r.hidden; });
    var anyPersonalized = false, complete = true, firstBlank = null;
    visible.forEach(function (r) {
      var box = r.querySelector('[data-biwpers-skip]');
      var el = r.querySelector('[data-biwpers-name]');
      var skipped = box && box.checked;
      if (!skipped) {
        anyPersonalized = true;
        if (!(el && (el.value || '').trim())) { complete = false; if (!firstBlank) firstBlank = el; }
      }
    });
    return { anyPersonalized: anyPersonalized, complete: complete, firstBlank: firstBlank,
      plural: visible.filter(function (r) {
        var b = r.querySelector('[data-biwpers-skip]'); return !(b && b.checked); }).length > 1 };
  }

  function syncConfirm(root) {
    var wrap = root.querySelector('[data-biwpers-confirm]');
    if (!wrap) return null;
    var box = wrap.querySelector('[data-biwpers-confirm-box]');
    var txt = wrap.querySelector('[data-biwpers-confirm-text]');
    var st = personState(root);
    // nothing is being printed, so there is nothing to confirm
    wrap.hidden = !st.anyPersonalized;
    if (txt) txt.textContent = st.plural
      ? 'I have reviewed the names above and confirmed the spelling is correct.'
      : 'I have reviewed the name above and confirmed the spelling is correct.';
    if (box) {
      box.disabled = !st.complete;
      if (!st.complete && box.checked) box.checked = false;
    }
    wrap.classList.toggle('is-na', !st.complete);
    return { state: st, box: box, wrap: wrap };
  }

  function start() {
    document.addEventListener('submit', guardSubmit, true);
    tick();
    setInterval(tick, 400);
    document.addEventListener('change', tick, true);
    document.addEventListener('click', function () { setTimeout(tick, 80); }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
